import * as XLSX from 'xlsx';

// BOQ Hub → Material Mappings: labels, colours and the small rules the screens share.
export const NAVY = '#0b318b';

// a mapping's status (null on a BOQ line = not mapped yet)
export const MAPPING_STATUS = {
  unmapped: { label: 'Unmapped', color: '#991b1b', bg: '#fee2e2' },
  draft: { label: 'Draft', color: '#475569', bg: '#f1f5f9' },
  pending_approval: { label: 'Pending Approval', color: '#a16207', bg: '#fef9c3' },
  returned: { label: 'Returned', color: '#c2410c', bg: '#ffedd5' },
  approved: { label: 'Approved', color: '#166534', bg: '#dcfce7' },
  rejected: { label: 'Rejected', color: '#b91c1c', bg: '#fee2e2' },
};
export const EDITABLE = ['draft', 'returned', 'rejected'];

export const MAPPING_TYPES = [
  { value: 'direct', label: 'Direct' },
  { value: 'component', label: 'Component' },
  { value: 'fitting', label: 'Fitting' },
  { value: 'accessory', label: 'Accessory' },
  { value: 'consumable', label: 'Consumable' },
  { value: 'misc_included', label: 'Miscellaneous — Included' },
  { value: 'non_boq', label: 'Non-BOQ' },
  { value: 'variation', label: 'Variation' },
  { value: 'pending', label: 'Pending Mapping' },
];

export const COVERAGE = [
  { value: 'explicit', label: 'Explicit BOQ Item' },
  { value: 'included_in_description', label: 'Included in BOQ Description' },
  { value: 'included_accessory', label: 'Included Accessory' },
  { value: 'included_fitting', label: 'Included Fitting' },
  { value: 'included_consumable', label: 'Included Consumable' },
  { value: 'included_misc', label: 'Included Miscellaneous' },
  { value: 'provisional', label: 'Provisional' },
  { value: 'variation', label: 'Variation' },
  { value: 'non_boq', label: 'Non-BOQ' },
  { value: 'pending_review', label: 'Pending Review' },
];

export const UNMAPPED_STATE = {
  unmapped: { label: 'Unmapped', color: '#991b1b', bg: '#fee2e2' },
  pending_mapping: { label: 'Mapping in progress', color: '#a16207', bg: '#fef9c3' },
  deferred: { label: 'Deferred', color: '#475569', bg: '#f1f5f9' },
  non_boq: { label: 'Non-BOQ', color: '#6d28d9', bg: '#ede9fe' },
  variation: { label: 'Variation', color: '#0369a1', bg: '#e0f2fe' },
};

export const typeLabel = (v) => MAPPING_TYPES.find(t => t.value === v)?.label || v || '—';
export const coverageLabel = (v) => COVERAGE.find(t => t.value === v)?.label || v || '—';

// Words in a material's name that say what part it plays in a BOQ line. The first rule that matches wins;
// anything else is the item itself (Direct, explicitly in the BOQ).
const ROLE_RULES = [
  { words: /\b(elbows?|bends?|tees?|reducers?|couplers?|couplings?|sockets?|unions?|nipples?|adaptors?|adapters?|flanges?|end caps?|caps?|plugs?|crosses?|valves?|glands?|lugs?|joints?|refnet)\b/, type: 'fitting', coverage: 'included_fitting' },
  { words: /\b(clips?|brackets?|hangers?|supports?|saddles?|clamps?|rods?|anchors?|rawl ?plugs?|labels?|tags?|markers?|boxes?|covers?|frames?|channels?|unistrut)\b/, type: 'accessory', coverage: 'included_accessory' },
  { words: /\b(cement|solvent|glue|adhesive|tape|sealant|silicone|screws?|bolts?|nuts?|washers?|nails?|ties?|grease|primer|paint|thinner|rags?|consumables?|welding|electrodes?|ptfe|sealing)\b/, type: 'consumable', coverage: 'included_consumable' },
];

/** The likely mapping type and BOQ coverage of a material for a BOQ line (a suggestion; the user decides). */
export function autoClassify(materialName = '', boqDescription = '') {
  const name = materialName.toLowerCase();
  const rule = ROLE_RULES.find(r => r.words.test(name));
  if (rule) {
    // a fitting/accessory/consumable the BOQ line names is still "included in the description"
    const mentioned = name.split(/[^a-z0-9]+/).some(w => w.length > 3 && boqDescription.toLowerCase().includes(w) && rule.words.test(w));
    return { mapping_type: rule.type, boq_coverage: mentioned ? 'included_in_description' : rule.coverage };
  }
  return { mapping_type: 'direct', boq_coverage: 'explicit' };
}

export const fmtQty = (v) => (v == null || v === '' ? '—' : Number(v).toLocaleString(undefined, { maximumFractionDigits: 3 }));

export const fmtDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

/** Rows to an .xlsx download. cols: [{ key, label, value?: row => any }] */
export function exportXlsx(rows, cols, filename, sheet = 'Sheet1') {
  const data = rows.map(r => Object.fromEntries(cols.map(c => [c.label, c.value ? c.value(r) : r[c.key]])));
  const ws = XLSX.utils.json_to_sheet(data, { header: cols.map(c => c.label) });
  ws['!cols'] = cols.map(c => ({ wch: Math.max(10, Math.min(60, c.label.length + 4)) }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheet.slice(0, 31));
  XLSX.writeFile(wb, filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`);
}

// the building / discipline / work package lists of a project, as Select options
export const asOptions = (list, idKey, all) => [
  { value: '', label: all },
  ...(list || []).map(x => ({ value: x[idKey] || x.id, label: x.name })),
];
